import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, User, Music } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert various video URLs to embed format
const getVideoEmbedUrl = (url) => {
  if (!url) return '';
  
  // YouTube watch URL
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // YouTube short URL
  if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // YouTube already embed format
  if (url.includes('youtube.com/embed/')) {
    return url;
  }
  
  // Vimeo
  if (url.includes('vimeo.com/') && !url.includes('player.vimeo.com')) {
    const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
    if (videoId) return `https://player.vimeo.com/video/${videoId}`;
  }
  
  // Vimeo already embed format
  if (url.includes('player.vimeo.com')) {
    return url;
  }
  
  // Return as-is for other URLs (may not work)
  return url;
};

const BlogPost = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API}/blog/${postId}`);
        if (res.ok) {
          const data = await res.json();
          setPost(data);
        } else if (res.status === 404) {
          setError('Blog post not found');
        } else {
          setError('Failed to load blog post');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-accent text-4xl text-foreground mb-4">{error}</h1>
          <Link to="/">
            <Button variant="outline" className="font-mono uppercase">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground font-mono text-sm uppercase mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Post header */}
        <header className="mb-8">
          <h1 className="font-accent text-4xl sm:text-5xl text-foreground mb-4">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground font-mono text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author_name}</span>
            </div>
            
            {post.published_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.published_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </header>

        {/* Featured band card */}
        {post.featured_band && (
          <div className="border-2 border-primary bg-primary/5 p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Music className="w-5 h-5 text-primary" />
              <span className="font-mono text-xs uppercase text-primary tracking-wider">Featured Band</span>
            </div>
            
            <div className="flex items-center gap-4">
              {post.featured_band.image_urls?.[0] && (
                <img 
                  src={post.featured_band.image_urls[0]} 
                  alt={post.featured_band.name}
                  className="w-20 h-20 object-cover border border-border"
                />
              )}
              <div>
                <h3 className="font-sans text-xl font-bold text-foreground">{post.featured_band.name}</h3>
                <Link 
                  to={`/band/${post.featured_band.band_id}`}
                  className="font-mono text-xs uppercase text-primary hover:underline"
                >
                  View Band Profile →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Featured Image */}
        {post.image_url && (
          <div className="mb-8">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full max-h-96 object-cover border-2 border-border"
            />
          </div>
        )}

        {/* Video Embed */}
        {post.video_embed_url && (
          <div className="mb-8 aspect-video">
            <iframe
              src={getVideoEmbedUrl(post.video_embed_url)}
              width="100%"
              height="100%"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
              className="border-2 border-border"
              title={`${post.title} video`}
            />
          </div>
        )}

        {/* Post content */}
        <div className="prose prose-invert max-w-none">
          <div className="font-mono text-base text-foreground/90 leading-relaxed whitespace-pre-wrap">
            {post.content}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-border">
          <Link to="/">
            <Button variant="outline" className="font-mono uppercase">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </footer>
      </article>
    </div>
  );
};

export default BlogPost;
